/**
 * useNoteAuditsList Hook
 *
 * Fetches and manages note audits list with pagination
 */

import { useMemo } from 'react';

import { useGetDashboardNotesQuery } from '../store/api';
import type { NoteAuditListItem } from '../types';

interface UseNoteAuditsListOptions {
  /** Filter by specific user ID */
  userId?: string;
  /** Filter by completion status */
  isCompleted?: boolean;
}

interface UseNoteAuditsListResult {
  /** List of note audits */
  audits: NoteAuditListItem[];
  /** Total number of audits */
  total: number;
  /** Current page */
  page: number;
  /** Items per page */
  perPage: number;
  /** Whether there are more items */
  hasMore: boolean;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
  /** Whether list is empty */
  isEmpty: boolean;
}

export const useNoteAuditsList = (
  options: UseNoteAuditsListOptions = {},
): UseNoteAuditsListResult => {
  // Note: The current API doesn't support filtering by userId on the provider endpoint
  // This would need backend support or use a different endpoint
  // Override query arg type until OpenAPI spec includes is_completed param
   
  const { data, isLoading, error, refetch } = useGetDashboardNotesQuery(
    (options.isCompleted !== undefined ? { is_completed: options.isCompleted } : undefined) as any,
  );

  const audits = useMemo(() => data?.audits || [], [data]);
  const total = data?.total || 0;
  const page = data?.page || 1;
  const perPage = data?.per_page || 20;
  const hasMore = data?.has_more || false;
  const isEmpty = !isLoading && audits.length === 0;

  return {
    audits,
    total,
    page,
    perPage,
    hasMore,
    isLoading,
    error: error ? 'Failed to load note audits' : null,
    refetch,
    isEmpty,
  };
};

export default useNoteAuditsList;
